// --- pages/Search.jsx ---
import React, { useEffect, useState, useRef } from 'react';

// Components
import SearchForm from '../../components/search/SearchForm';
import AdvancedFilters from '../../components/search/AdvancedFilters';
import LocationFallback from '../../components/search/LocationFallback';
import SearchResults from '../../components/search/SearchResults';
import SponsoredCard from '../../components/search/SponsoredCard';
import HorseStatusMap from '../../components/search/HeatMap';

// Helpers
import qs from 'qs';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export default function Search() {
  const [zip, setZip] = useState('');
  const [radius, setRadius] = useState('');
  const [sort, setSort] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(false);
  const [sponsored, setSponsored] = useState([]);
  const [userCoords, setUserCoords] = useState(null);
  const [sponsoredError, setSponsoredError] = useState(false);
  const [fallbackMsg, setFallbackMsg] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dropdownData, setDropdownData] = useState({});
  const [breeds, setBreeds] = useState([]);
  const [filters, setFilters] = useState({
    breed: [],
    color: [],
    sex: [],
    discipline: [],
    minPrice: '',
    maxPrice: '',
    minHeight: '',
    maxHeight: '',
    minAge: '',
    maxAge: '',
    vetCheck: false,
    verifiedSeller: false
  });

  const { user } = useAuth();
  const resultsRef = useRef(null);
  const [scrollTrigger, setScrollTrigger] = useState(false);

  const cleanString = (str) =>
    typeof str === 'string' ? str.replace(/[^\x20-\x7E]/g, '') : '';

  const fetchDropdowns = async () => {
    try {
      const dropdownRes = await api.get('/dropdowns/horseAttributes');
      const breedRes = await api.get('/breeds');
      setDropdownData(dropdownRes.data?.fields || dropdownRes.data || {});
      setBreeds(breedRes.data || []);
    } catch (err) {
      console.error('❌ Failed to fetch dropdown options:', err);
    }
  };

  const fetchSponsored = async (lat, lon) => {
    try {
      const hasCoords = typeof lat === 'number' && typeof lon === 'number';
      const endpoint = hasCoords
        ? `/horses/sponsored?lat=${lat}&lon=${lon}`
        : '/horses/sponsored';
      const res = await api.get(endpoint);

      const rawList = res.data?.horses || res.data || [];
      const normalized = rawList
        .filter(h => h.available === true)
        .map((h) => ({
          _id: h._id,
          status: h.status,
          horseInfo: h.horseInfo || {},
          saleInfo: h.saleInfo || {},
          location: h.location || {}
        }));

      setSponsored(normalized);
      setFallbackMsg(cleanString(res.data?.message || ''));
      if (!Array.isArray(normalized) || normalized.length === 0)
        setSponsoredError(true);
    } catch (err) {
      console.error('❌ Failed to fetch sponsored horses:', err);
      setSponsoredError(true);
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (
      userCoords &&
      typeof userCoords.lat === 'number' &&
      typeof userCoords.lon === 'number'
    ) {
      fetchSponsored(userCoords.lat, userCoords.lon);
    }
  }, [userCoords?.lat, userCoords?.lon]);

  const handleSearch = async () => {
    if (!zip || !radius) {
      alert('Please enter a zip code and radius.');
      return;
    }
  
    const activeFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) =>
        typeof v === 'boolean' ? v === true : Array.isArray(v) ? v.length > 0 : v !== ''
      )
    );
  
    const searchParams = {
      zip,
      radius,
      sort,
      ...activeFilters,
      status: ['For Sale', 'Active Bid'],
      page: 1
    };
  
    try {
      const res = await api.get('/search', {
        params: searchParams,
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' })
      });
  
      setSearchResults(res.data.horses || res.data);
      setSearchError(res.data.length === 0);
      setShowAdvanced(false);
      setScrollTrigger(true); // ✅ trigger scroll on search
    } catch (err) {
      console.error('❌ Search failed with zip/radius:', err);
      setSearchError(true);
    }
  };

  useEffect(() => {
    if (scrollTrigger) {
      const timeout = setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        setScrollTrigger(false); // reset flag
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [scrollTrigger]);

  const cleanZip = cleanString(zip);
  const cleanRadius = cleanString(radius);
  const uniqueSponsored = sponsored.filter(
    (horse, index, self) =>
      horse._id && index === self.findIndex((h) => h._id === horse._id)
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-4xl font-medium mb-2 font-cormorant text-primary">
          Let's Find Your Next Horse
        </h1>

        <SearchForm
          zip={zip}
          setZip={setZip}
          radius={radius}
          setRadius={setRadius}
          sort={sort}
          setSort={setSort}
          onSearch={handleSearch}
        />

        {/* Filter breadcrumbs */}
        {Object.entries(filters).some(([key, val]) =>
          typeof val === 'boolean' ? val : val !== '' && val.length !== 0
        ) && (
          <div className="flex flex-wrap gap-2 mb-3">
            {Object.entries(filters).map(([key, val]) => {
              const display = Array.isArray(val)
                ? val.join(', ')
                : typeof val === 'boolean'
                ? val ? 'Yes' : ''
                : val;
              return display ? (
                <span
                  key={key}
                  className="bg-sage text-white text-sm px-3 py-1 rounded-full flex items-center gap-1"
                >
                  {(() => {
                    const label = key.charAt(0).toUpperCase() + key.slice(1);
                    if (key === 'breed') {
                      const names = (Array.isArray(val) ? val : [val])
                        .map((id) => breeds.find((b) => b._id === id)?.name || id)
                        .join(', ');
                      return `${label}: ${names}`;
                    }
                    return `${label}: ${display}`;
                  })()}
                  <button
                    onClick={() => {
                      const newFilters = { ...filters };
                      newFilters[key] = Array.isArray(val)
                        ? []
                        : typeof val === 'boolean'
                        ? false
                        : '';
                      setFilters(newFilters);
                    }}
                    className="ml-1 text-xs"
                  >
                    ✕
                  </button>
                </span>
              ) : null;
            })}
          </div>
        )}
      </div>

      {/* Dropdown toggle for advanced filters */}
      <div className="relative mb-4">
        <button
          onClick={() => setShowAdvanced((prev) => !prev)}
          className="w-full border border-gray-300 rounded px-4 py-2 text-left bg-white shadow-sm hover:border-gray-400 font-montserrat font-bold"
        >
          {showAdvanced ? 'Hide Advanced Filters ▲' : 'Show Advanced Filters ▼'}
        </button>

        {showAdvanced && (
          <div className="w-full mt-2 border border-gray-200 bg-white rounded shadow-md p-4">
            <AdvancedFilters
              filters={filters}
              setFilters={setFilters}
              dropdownData={dropdownData}
              breeds={breeds}
            />
          </div>
        )}
      </div>

      <LocationFallback userCoords={userCoords} setUserCoords={setUserCoords} />

      {uniqueSponsored.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-montserrat font-bold mb-3">Sponsored Horses</h2>
          {fallbackMsg && (
            <p className="text-sm text-gray-600 italic mb-2">{fallbackMsg}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {uniqueSponsored.map((horse) => (
              <SponsoredCard key={horse._id} horse={horse} />
            ))}
          </div>
        </div>
      )}

      {sponsoredError && (
        <p className="text-gray-500 italic text-sm">
          No sponsored horses available right now.
        </p>
      )}
      <div ref={resultsRef}>
        <SearchResults
          results={searchResults}
          setResults={setSearchResults}
          filters={filters}
          zip={cleanZip}
          radius={cleanRadius}
          sort={sort}
          user={user}
        />
      </div>
    </div>
  );
}